import { useCallback } from "react";
import { getSecondWellIsOpen, setSecondWellIsOpen } from "../../store/slices/appSlice";
import DropdownMenu from "../../components/common/DropdownMenu";
import { HorizontalWellInputs } from "./HorizontalWellInputs";
import type { IIPRWell, IPRCalculationType } from "./interfaces";
import { JonesInputs } from "./JonesInputs";
import { VerticalWellInputs } from "./VerticalWellInputs";
import { useAppDispatch, useAppSelector } from "../../store/hooks/hooks";
import { setFieldValue, setWell1Type, setWell2Type, updateFieldByUnit } from "../../store/slices/iprCalculatorSlice";
import { useTranslation } from "react-i18next";

interface IProps {
	well: IIPRWell;
}

export const WellInputs = ({ well }: IProps) => {
	const dispatch = useAppDispatch();
	const secondWellOnScreen = useAppSelector(getSecondWellIsOpen);
	const { t } = useTranslation();

	const handleSelectCalculatorType = useCallback((selectedType: string) => {
		if (well.id === "well1")
			dispatch(setWell1Type(selectedType as IPRCalculationType));

		if (well.id === "well2")
			dispatch(setWell2Type(selectedType as IPRCalculationType));

	}, [dispatch, well]);

	const toggleSecondWell = () => {
		dispatch(setSecondWellIsOpen(!secondWellOnScreen));
	}

	const onInputChange = useCallback(
		(inputName: string, value: number | null) => {
			dispatch(
				setFieldValue({ wellId: well.id, inputName: inputName, value: value })
			);
		},
		[dispatch, well]
	);

	const onSelectUnit = useCallback((inputId: string, selectedUnitId: string) => {
		dispatch(
			updateFieldByUnit({
				wellId: well.id,
				inputId: inputId,
				selectedUnitId: selectedUnitId,
			})
		);
	}, [dispatch, well]);

	return <>
		<fieldset className="ipr__well-inputs">
			<legend>{well.id === "well1" ? t(`ipr:well1_title`) : t(`ipr:well2_title`)}</legend>

			{(well.id === "well2" || !secondWellOnScreen) && <button className="ipr__add-second-well-btn"
				title={well.id === "well1" ? t(`ipr:add_well_title`) : t(`ipr:collapse_well_title`)}
				onClick={toggleSecondWell}
			>{well.id === "well1" ? '+' : '−'}
			</button>}

			<div className="ipr__well-header">
				<DropdownMenu
					className="ipr__well-type"
					inputId={"ipr-input-calculation-type"}
					items={['vertical', 'horizontal', 'jones'].map(type => ({ id: type, translationKey: `ipr:calculation_types.${type}` }))}
					selectedItemId={well.calculationType}
					onSelect={handleSelectCalculatorType}
				/>
			</div>
			{well.calculationType == "horizontal" && <HorizontalWellInputs wellId={well.id} onInputChange={onInputChange} onSelectUnit={onSelectUnit} />}
			{well.calculationType == "vertical" && <VerticalWellInputs wellId={well.id} onInputChange={onInputChange} onSelectUnit={onSelectUnit} />}
			{well.calculationType == "jones" && <JonesInputs wellId={well.id} onInputChange={onInputChange} onSelectUnit={onSelectUnit} />}
		</fieldset>
	</>;
};